<template>
  <div>
    <Head :title="itemPackage.code" />
    <h1 class="mb-8 text-3xl font-bold">
      Master
      <span class="text-indigo-400 font-medium"> / </span>
      <Link class="text-indigo-400 hover:text-indigo-600" href="/packages">
        Paket
      </Link>
      <span class="text-indigo-400 font-medium"> / </span>
      {{ itemPackage.name }}
    </h1>
    <div class="max-w-3xl bg-white rounded-md shadow overflow-hidden">
      <form @submit.prevent="update">
        <div class="flex flex-wrap -mb-8 -mr-6 p-8">
          <div class="flex pb-4 pr-6 w-full">
            <label class="form-label mr-4">Referensi PR:</label>
            <Link :href="purchaseRequestMaster ? `/purchases/${purchaseRequestMaster.id}/edit` : '#'" :class="purchaseReqLinkClass">
              <b>{{ purchaseRequestMaster ? purchaseRequestMaster.code : 'N/A' }}</b>
            </Link>
          </div>
          <text-input
            v-model="form.code"
            :error="form.errors.code"
            class="pb-8 pr-6 w-full md:w-1/2"
            label="Code" />
          <text-input
            v-model="form.name"
            :error="form.errors.name"
            class="pb-8 pr-6 w-full md:w-1/2"
            label="Name" />
          <textarea-input
            v-model="form.desc"
            :error="form.errors.desc"
            class="pb-8 pr-6 w-full lg:w-1/2"
            label="Description" />
        </div>
        <div class="flex items-center justify-between px-8 py-4 bg-gray-50 border-t border-gray-100">
          <button
            v-if="!itemPackage.deleted_at"
            class="text-red-600 hover:underline"
            tabindex="-1"
            type="button"
            @click="destroy">
            Delete Paket
          </button>
          <loading-button
            :loading="form.processing"
            class="btn-indigo"
            type="submit">
            Update Paket
          </loading-button>
        </div>
      </form>
    </div>

    <h2 class="mt-12 text-2xl font-bold">
      Details
    </h2>
    <div class="flex justify-between my-5">
      <button class="btn-indigo" @click="createMode = true">
        <span>Create</span> <span class="hidden md:inline">&nbsp;Detail</span>
      </button>
    </div>
    <div class="mt-6 bg-white rounded shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <detail
          v-for="(detail, idx) in details"
          :key="detail.id"
          :row-index="++idx"
          :editable-mode="detail.editable"
          :package-detail="detail"
          :errors="$page.props.errors"
          :units="units"
          @destroy:detail="destroyDetail"
          @edit:detail="editDetail"
          @cancel="cancel('edit')"
          @save="updateDetail" />
        <detail
          v-if="createMode"
          :key="0"
          :editable-mode="createMode"
          :package-detail="editedItem"
          :units="units"
          :errors="$page.props.errors"
          @cancel="cancel('create')"
          @save="createDetail" />
        <tr v-if="details.length === 0">
          <td class="px-6 py-4 border-t" colspan="5">
            No details found.
          </td>
        </tr>
      </table>
    </div>
    <div v-if="details.length >= 1" class="flex justify-between my-5">
      <button class="btn-indigo" @click="createMode = true">
        <span>Create</span> <span class="hidden md:inline">&nbsp;Detail</span>
      </button>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import LoadingButton from '@/Shared/LoadingButton'
import TextareaInput from '@/Shared/TextareaInput'
import 'flatpickr/dist/flatpickr.css'
import Detail from '@/Pages/Packages/DetailRow'
import mapValues from 'lodash/mapValues'
import find from 'lodash/find'

export default {
    components: {
        Head,
        Link,
        LoadingButton,
        TextInput,
        TextareaInput,
        Detail,
    },
    layout: Layout,
    props: {
        itemPackage: Object,
        purchaseRequestMaster: Object,
        details: Array,
        units: Array,
    },
    remember: 'form',
    data () {
        return {
            form: this.$inertia.form({
                code: this.itemPackage.code,
                name: this.itemPackage.name,
                desc: this.itemPackage.desc,
            }),
            createMode: false,
            editedItem: {
                id: null,
                item_id: null,
                unit_id: null,
                qty: null,
                deleted_at: null,
                editable: false,
            },
            detailKeyRefresh: 0,
        }
    },
    computed: {
        isPurchaseReqAvailable () {
            return this.purchaseRequestMaster !== null && this.purchaseRequestMaster !== undefined
        },
        purchaseReqLinkClass () {
            return this.isPurchaseReqAvailable ? 'text-indigo-400 hover:text-indigo-600 cursos-pointer' : 'pointer-events-none cursor-default text-gray'
        },
    },
    methods: {
        update () {
            this.form.post(`/packages/${this.itemPackage.id}`, {
                preserveScroll: true,
            })
        },
        destroy () {
            if (confirm('Are you sure you want to delete this package?')) {
                this.$inertia.delete(`/packages/${this.itemPackage.id}`)
            }
        },
        restore () {
            if (confirm('Are you sure you want to restore this package?')) {
                this.$inertia.put(`/packages/${this.itemPackage.id}/restore`)
            }
        },
        reset () {
            this.searchform = mapValues(this.searchform, () => null)
        },
        editDetail (detail) {
            let edited = find(this.details, ['id', detail.id])
            edited.editable = true
        },
        destroyDetail (detailId) {
            this.$inertia.delete(`/packagedetails/${detailId}`)
        },
        resetEditedItem () {
            this.editedItem = {
                id: null,
                item: null,
                unit: null,
                qty: 0,
                editable: false,
            }
            this.createMode = false
        },
        cancel (action) {
            if (action === 'create') this.resetEditedItem()
            if (action === 'edit') this.$inertia.reload({ only: ['details'] })
        },
        createDetail (value) {
            const formDetail = this.$inertia.form({
                package_m_id: this.itemPackage.id,
                item_id: value.item.id,
                unit_id: value.unit.id,
                qty: value.qty,
            })
            formDetail.post('/packagedetails', {
                preserveScroll: true,
                onSuccess: () => {
                    this.resetEditedItem()
                },
                onError: () => {
                    this.editedItem = value
                },
            })
        },
        updateDetail (value) {
            const formDetail = this.$inertia.form({
                package_m_id: this.itemPackage.id,
                item_id: value.item.id,
                unit_id: value.unit.id,
                qty: value.qty,
            })
            formDetail.put(`/packagedetails/${value.id}`, {
                preserveScroll: true,
                onError: () => {
                    this.editedItem = value
                },
            })
        },
    },
}
</script>
